/** Config workspace endpoints (CONTRACT §2). The user's editable configs. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "./client";
import { useToast } from "../components/Toast";
import type {
  CloneConfigPayload,
  Config,
  ConfigSummary,
  ConfigVersion,
  CreateConfigPayload,
  EddConfig,
  MergeConfigPayload,
  ValidationReport,
} from "./types";

export function useConfigs(locationId?: string) {
  return useQuery({
    queryKey: locationId ? ["configs", { location_id: locationId }] : ["configs"],
    queryFn: async (): Promise<ConfigSummary[]> => {
      const { data } = await api.get<ConfigSummary[]>("/configs", {
        params: locationId ? { location_id: locationId } : {},
      });
      return data;
    },
  });
}

export function useConfig(id: string | undefined) {
  return useQuery({
    queryKey: ["config", id],
    queryFn: async (): Promise<Config> => {
      const { data } = await api.get<Config>(`/configs/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useConfigVersions(id: string | undefined) {
  return useQuery({
    queryKey: ["config-versions", id],
    queryFn: async (): Promise<ConfigVersion[]> => {
      const { data } = await api.get<ConfigVersion[]>(`/configs/${id}/versions`);
      return data;
    },
    enabled: !!id,
  });
}


/** Surface the server-side duplicate-name rename: the backend keeps EDD names unique per
 *  org by appending a numeric suffix — make that visible instead of silently renaming. */
function useRenameNotice() {
  const toast = useToast();
  const { t } = useTranslation();
  return (requested: string | undefined, created: string) => {
    const want = (requested ?? "").trim();
    if (want && want.toLowerCase() !== created.toLowerCase()) {
      toast.info(t("configs.renamedNotice", { requested: want, created }));
    }
  };
}

export function useCreateConfig() {
  const qc = useQueryClient();
  const notifyRenamed = useRenameNotice();
  return useMutation({
    mutationFn: async (payload: CreateConfigPayload): Promise<Config> => {
      const { data } = await api.post<Config>("/configs", payload);
      notifyRenamed(payload.name, data.name);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

/** Publish a DRAFT config so the whole org can use it (the end of the AI-creation gate). */
export function usePublishConfig(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<Config> => {
      const { data } = await api.post<Config>(`/configs/${id}/publish`, {});
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["config", id], data);
      void qc.invalidateQueries({ queryKey: ["configs"] });
      void qc.invalidateQueries({ queryKey: ["config-versions", id] });
      // The version bump stales test results — refetch tests AND the config (the
      // mutation response carries default test aggregates, not the real ones).
      void qc.invalidateQueries({ queryKey: ["config-tests", id] });
      void qc.invalidateQueries({ queryKey: ["config", id] });
    },
  });
}

export function useSaveConfig(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (edd: EddConfig): Promise<Config> => {
      const { data } = await api.put<Config>(`/configs/${id}`, { edd });
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["config", id], data);
      void qc.invalidateQueries({ queryKey: ["configs"] });
      void qc.invalidateQueries({ queryKey: ["config-versions", id] });
      // Saving bumps the version: previously-passed tests are now stale — refetch.
      void qc.invalidateQueries({ queryKey: ["config-tests", id] });
      void qc.invalidateQueries({ queryKey: ["config", id] });
    },
  });
}

export function useValidateConfig(id: string) {
  return useMutation({
    mutationFn: async (edd: EddConfig): Promise<ValidationReport> => {
      const { data } = await api.post<ValidationReport>(`/configs/${id}/validate`, {
        edd,
      });
      return data;
    },
  });
}

export function useCloneConfig() {
  const qc = useQueryClient();
  const notifyRenamed = useRenameNotice();
  return useMutation({
    mutationFn: async (args: { id: string; payload: CloneConfigPayload }): Promise<Config> => {
      const { data } = await api.post<Config>(`/configs/${args.id}/clone`, args.payload);
      notifyRenamed(args.payload.name, data.name);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

export function useDeleteConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/configs/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

export function useMergeConfigs() {
  const qc = useQueryClient();
  const notifyRenamed = useRenameNotice();
  return useMutation({
    mutationFn: async (payload: MergeConfigPayload): Promise<Config> => {
      const { data } = await api.post<Config>("/configs/merge", payload);
      notifyRenamed(payload.name, data.name);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}
